// lib/ksaTime.js — جولة تصحيح 2026-10-14 (المرحلة 3) · البند 8.
// مُحدَّث: جولة تصحيح «حوكمة الإرسال» (أ٢) — تاريخ ميلادي مضمون في كل متصفح.
//
// helper التنسيق الموحّد الوحيد بتوقيت KSA (Asia/Riyadh) — صريح عبر
// Intl.DateTimeFormat بـtimeZone ثابت، بصرف النظر عن توقيت جهاز المستخدم
// (لا يعتمد على أن يكون المتصفح نفسه مضبوطاً على الرياض). كل تاريخ/وقت في
// كل شاشة بالمنصة يجب أن يمرّ من هنا — لا نداء Intl/toLocale* مباشر خارج
// هذا الملف (حارس دائم: tests/test_item_a2_gregorian_date_guard.py).
//
// السبب الجذري المُصلَح هنا: locale "ar-SA" بلا calendar صريح — بعض
// المتصفحات (Safari/WebKit تحديداً) تُحوّل التقويم الافتراضي لـ"ar-SA" إلى
// islamic-umalqura (هجري)، بينما V8/Chrome قد يُعيد ميلادياً. النتيجة قبل
// الإصلاح: تاريخ هجري ظاهر في الإنتاج على بعض الأجهزة فقط («٠٣ جمادى
// الأولى ١٤٤٨ هـ»). الإصلاح: `calendar: "gregory"` صريح دائماً — لا اعتماد
// على افتراضي أي متصفح. `numberingSystem: "latn"` لمطابقة النمط المستخدم
// فعلياً في بقية المنصة (أرقام غربية 0-9 — نفس الاختيار القائم في عشرات
// نقاط التنسيق الأخرى عبر اللاحقة `-u-nu-latn`، وليس اختراعاً جديداً).

const KSA_TZ = "Asia/Riyadh";
const KSA_LOCALE = "ar-SA";

function _toDate(value) {
  if (value instanceof Date) return value;
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "14-10-2026 08:03 ص" (بالعربية، ميلادي دائماً) أو "—" إن كانت القيمة غير صالحة. */
export function formatKSA(iso, { withSeconds = false } = {}) {
  const d = _toDate(iso);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(KSA_LOCALE, {
      timeZone: KSA_TZ, calendar: "gregory", numberingSystem: "latn",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
      ...(withSeconds ? { second: "2-digit" } : {}),
      hour12: true,
    }).format(d);
  } catch (_e) {
    return "—";
  }
}

/** وقت فقط (بلا تاريخ) بتوقيت KSA — لمثل "آخر تحديث". */
export function formatKSATime(iso) {
  const d = _toDate(iso);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(KSA_LOCALE, {
      timeZone: KSA_TZ, calendar: "gregory", numberingSystem: "latn",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: true,
    }).format(d);
  } catch (_e) {
    return "—";
  }
}

/**
 * منسِّق تاريخ عام (بديل مباشر لـ`X.toLocaleDateString("ar...", opts)`) —
 * ميلادي دائماً + توقيت KSA + أرقام لاتينية، مهما كانت `options` الممرَّرة
 * (dateStyle/weekday/month/day/...). كل الصفحات تستخدم هذا، لا نداءً مباشراً.
 */
export function formatKSADate(value, options = {}) {
  const d = _toDate(value);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(KSA_LOCALE, {
      timeZone: KSA_TZ, calendar: "gregory", numberingSystem: "latn",
      ...options,
    }).format(d);
  } catch (_e) {
    return "—";
  }
}

/** بديل مباشر لـ`X.toLocaleString("ar...", opts)` (تاريخ+وقت معاً). */
export function formatKSADateTime(value, options = {}) {
  return formatKSADate(value, options);
}

/**
 * تاريخ هجري **صريح ومتعمَّد** — يُستخدَم فقط حين يكون عرض التقويم الهجري
 * نفسه هو الهدف المطلوب (مثال: شريط ميلادي+هجري معاً في لوحة CEO)، لا
 * الافتراضي بالخطأ. مذكور بالاسم حتى يكون واضحاً أنه ليس العلّة المُصلَحة
 * أعلاه — هذا استخدام هجري مقصود، موثَّق، ومعزول هنا وحده.
 */
export function formatHijriKSA(value, options = {}) {
  const d = _toDate(value);
  if (!d) return "—";
  try {
    return new Intl.DateTimeFormat(`${KSA_LOCALE}-u-ca-islamic-umalqura`, {
      timeZone: KSA_TZ, numberingSystem: "latn",
      ...options,
    }).format(d);
  } catch (_e) {
    return "—";
  }
}

// ⑤/L3-أ — الحساب التقويمي بتوقيت السعودية (لا توقيت الجهاز). كل «يوم»
// نص YYYY-MM-DD لليوم التقويمي في الرياض؛ الحساب عليه بتوقيت UTC الصافي فلا
// يتأثر بمنطقة الجهاز ولا بالتوقيت الصيفي. بداية الأسبوع إعداد
// (week_start_day من إعدادات المنشأة) والقيمة الأولية WEEK_START_DEFAULT.
export const WEEK_START_DEFAULT = "sunday";
const _WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const _PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: KSA_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});
const _pad = (n) => String(n).padStart(2, "0");
const _dayToUTC = (day) => { const [y, m, d] = String(day).slice(0, 10).split("-").map(Number); return Date.UTC(y, m - 1, d); };
const _utcToDay = (ms) => { const x = new Date(ms); return `${x.getUTCFullYear()}-${_pad(x.getUTCMonth() + 1)}-${_pad(x.getUTCDate())}`; };

export function weekStartIndex(v) {
  if (typeof v === "number" && v >= 0 && v <= 6) return v;
  const i = _WEEKDAYS.indexOf(String(v || WEEK_START_DEFAULT).toLowerCase());
  return i >= 0 ? i : _WEEKDAYS.indexOf(WEEK_START_DEFAULT);
}

export function ksaParts(value = new Date()) {
  const p = Object.fromEntries(_PARTS.formatToParts(_toDate(value) || new Date()).map((x) => [x.type, x.value]));
  const day = `${p.year}-${p.month}-${p.day}`;
  return { day, year: +p.year, month: +p.month, date: +p.day, hour: +p.hour, minute: +p.minute,
           dow: new Date(_dayToUTC(day)).getUTCDay() };
}
export const ksaToday = (now = new Date()) => ksaParts(now).day;
export const ksaHour = (now = new Date()) => ksaParts(now).hour;
export const ksaAddDays = (day, n) => _utcToDay(_dayToUTC(day) + n * 86400000);
export const ksaDaysAgo = (n, now = new Date()) => ksaAddDays(ksaToday(now), -n);
export const ksaDayOfWeek = (day) => new Date(_dayToUTC(day)).getUTCDay();
export const ksaDaysBetween = (a, b) => Math.round((_dayToUTC(b) - _dayToUTC(a)) / 86400000);
export function ksaStartOfWeek(day, weekStart = WEEK_START_DEFAULT) {
  return ksaAddDays(day, -((ksaDayOfWeek(day) - weekStartIndex(weekStart) + 7) % 7));
}
export const ksaStartOfMonth = (day) => `${String(day).slice(0, 7)}-01`;
export function ksaEndOfMonth(day) {
  const [y, m] = String(day).slice(0, 7).split("-").map(Number);
  return _utcToDay(Date.UTC(y, m, 0));
}
export const ksaDaysInMonth = (day) => Number(ksaEndOfMonth(day).slice(8, 10));
export const ksaStartOfYear = (day) => `${String(day).slice(0, 4)}-01-01`;

// AD1 (2026-10) — ربع/نصف سنوي تقويمي (Q1 يناير-مارس … H1 يناير-يونيو)،
// نفس أسلوب ksaStartOfMonth/ksaEndOfMonth (توقيت KSA، بلا حساب موازٍ).
export function ksaStartOfQuarter(day) {
  const [y, m] = String(day).slice(0, 7).split("-").map(Number);
  const qStartMonth = Math.floor((m - 1) / 3) * 3 + 1;
  return `${y}-${_pad(qStartMonth)}-01`;
}
export function ksaEndOfQuarter(day) {
  const s = ksaStartOfQuarter(day);
  return ksaEndOfMonth(ksaAddDays(`${s.slice(0, 7)}-01`, 62));
}
export function ksaStartOfHalf(day) {
  const [y, m] = String(day).slice(0, 7).split("-").map(Number);
  return `${y}-${_pad(m <= 6 ? 1 : 7)}-01`;
}
export function ksaEndOfHalf(day) {
  const s = ksaStartOfHalf(day);
  return ksaEndOfMonth(ksaAddDays(`${s.slice(0, 7)}-01`, 155));
}

// X1 — الدالة الوحيدة لعرض نطاق وقت (لا «HH:MM–HH:MM» محلي في المكوّنات: يُقلب اتجاهه داخل نص عربي).
export const formatTimeRange = (start, end) => `من ${start} إلى ${end}`;
